'use client';
import {HTMLAttributes} from 'react';
import MediaFilterImage from '@/components/Media/MediaFilterImage';
import {AnimatedLink} from '../AnimatedLink';
import {MapPinButton} from '../MapPinButton';

interface ISingleCarouselCardProps extends HTMLAttributes<HTMLDivElement> {
  info: {
    // Static image, shown when bannerMedia has no file.
    banner: string;
    title: string;
    body: string;
    buttonText: string;
    url: string;
  };
  sizes: string;
  bannerMedia?: Media | null;
  guideItem?: GuideItem;
}

export function SingleCarouselCard({
  info,
  className,
  sizes,
  bannerMedia,
  guideItem = {} as GuideItem,
}: ISingleCarouselCardProps) {
  const stripTags = info.body.replace(/(<([^>]+)>)/gi, '');
  let description = stripTags.slice(0, 160);

  if (stripTags.length > 160) {
    const nextSpaceIndex = stripTags.indexOf(' ', 160);
    if (nextSpaceIndex !== -1) {
      description = stripTags.slice(0, nextSpaceIndex);
    }
    description += '...';
  }

  return (
    <div
      className={`
        flex w-full relative aspect-[11/16] desktop:aspect-auto mx-auto
        text text-gray-900 text-lg flex-wrap whitespace-normal before:hidden before:w-full
        before:pb-[calc(79.44%*.6588)] before:content-[''] desktop:before:block desktop:block
        ${className}
      `}>
      <div
        className="
          ease-in-out duration-1000 top-0 right-0 h-full relative w-full
          desktop:group-[.is-focused]:absolute desktop:opacity-30 group-[.show-preview]:!min-w-0
          desktop:group-[.is-focused]:w-[79.44%] [@media(min-width:105.625em)]:min-w-[960px]
          desktop:group-[.is-focused]:opacity-100
          ">
        <MediaFilterImage
          media={bannerMedia}
          filterName={['1280w_webp', '1920w']}
          createFilterName="1280w_webp"
          fallbackSrc={info.banner}
          alt="villas"
          quality={50}
          fill
          sizes={sizes}
          className='object-cover h-full'
        />
      </div>

      <div
        className={`
          flex flex-col flex-1
          desktop:items-start desktop:justify-between
          items-center justify-center
          ease-in-out duration-200 p-[30px] desktop:px-10
          absolute left-0 top-0 h-full bg-shadow
          desktop:shadow-xl desktop:h-auto
        desktop:bg-white desktop:w-[30rem] desktop:top-14 desktop:opacity-0
          desktop:group-[.is-focused]:opacity-100
        `}>
        {guideItem.surrounding && (
          <MapPinButton
            className="!text-primary top-[30px] !right-10"
            guideItem={guideItem}
          />
        )}
        <h3 className="w-full mb-5 mt-9 text-ellipsis font-averta-semibold text-xl h-fit text-center desktop:text-start font-semibold text-white desktop:text-gray-900 desktop:text-4xl">
          {info.title}
        </h3>
        <div className="max-h-[120px] text-center desktop:text-start text-base overflow-hidden">
          <span className="max-w-full mb-0 h-max text-white desktop:text-gray-900">
            {description}
          </span>
        </div>
        {info.buttonText && (
          <AnimatedLink
            href={info.url}
            className="w-full text-center desktop:text-start desktop:w-fit px-[10px] desktop:px-10">
            {info.buttonText}
          </AnimatedLink>
        )}
      </div>
    </div>
  );
}
